// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module ErrorState
 * @package @geenius/analytics/solidjs-css
 * @description Internal recovery surface shared by SolidJS CSS analytics data
 * components when a fetch or rendering boundary fails.
 */

import { Button } from "@geenius/ui/solidjs-css";
import { type Component, Show } from "solid-js";
import { cx } from "../lib/cx";

/** Error value accepted by SolidJS CSS data-rendering components. */
export type AnalyticsErrorStateValue = Error | string | null | undefined;

/** Returns a user-visible message for a component error state. */
export function getAnalyticsErrorMessage(
  error: AnalyticsErrorStateValue,
): string {
  if (error instanceof Error && error.message.trim()) return error.message;
  if (typeof error === "string" && error.trim()) return error;
  return "Analytics data could not be loaded.";
}

interface ErrorStateProps {
  error: AnalyticsErrorStateValue;
  title?: string;
  onRetry?: () => void;
  class?: string;
}

/** Renders a compact, accessible error state with optional retry recovery. */
export const ErrorState: Component<ErrorStateProps> = (props) => (
  <div
    class={cx("gn-analytics-error-state", props.class)}
    data-analytics-error-state
    role="alert"
    aria-live="assertive"
  >
    <strong class="gn-analytics-error-state__title">
      {props.title ?? "Unable to load analytics"}
    </strong>
    <p class="gn-analytics-error-state__message">
      {getAnalyticsErrorMessage(props.error)}
    </p>
    <Show when={props.onRetry}>
      {(onRetry) => (
        <Button
          type="button"
          variant="secondary"
          class="gn-analytics-error-state__retry"
          onClick={onRetry()}
        >
          Retry
        </Button>
      )}
    </Show>
  </div>
);
